import { Chip, Tooltip } from "@mui/material";
import { useState } from "react";
import { SalvosCustomerOrderApi } from "src/api/generated/tenants/salvos/erp/sales/customerOrder/salvosCustomerOrderApi.ts";
import type { TransportPlanningDeliveryStatusRendererParams } from "src/tenantCustomizations/TenantCustomizations.ts";
import { logError } from "src/errorHandling/errorLogging.ts";
import { localizeUnsafe } from "src/utils/localizationUtils.ts";

const FOUNDATIONS_STATUS_CODE = "FOUNDATIONS";

export const salvosTransportPlanningDeliveryStatusRenderer = ({
	status,
	customerOrder,
	refreshAll,
}: TransportPlanningDeliveryStatusRendererParams) => {
	if (status.code != FOUNDATIONS_STATUS_CODE || customerOrder == null) return;

	return (
		<SalvosFoundationsStatusChip
			status={status}
			customerOrderId={customerOrder.customerOrderId}
			refreshAll={refreshAll}
		/>
	);
};

interface SalvosFoundationsStatusChipProps {
	status: TransportPlanningDeliveryStatusRendererParams["status"];
	customerOrderId: number;
	refreshAll: TransportPlanningDeliveryStatusRendererParams["refreshAll"];
}

const SalvosFoundationsStatusChip = ({ status, customerOrderId, refreshAll }: SalvosFoundationsStatusChipProps) => {
	const [disabledTooltipOpen, setDisabledTooltipOpen] = useState(false);
	const tooltipKey = status.clickable ? status.tooltipKey : status.disabledTooltipKey ?? status.tooltipKey;
	const tooltipText = tooltipKey != null ? localizeUnsafe(tooltipKey) : undefined;

	return (
		<Tooltip
			title={tooltipText ?? ""}
			arrow
			open={disabledTooltipOpen || undefined}
			onClose={() => setDisabledTooltipOpen(false)}
			disableHoverListener={tooltipText == null}
			disableTouchListener={tooltipText == null}
			disableFocusListener={tooltipText == null}
		>
			<Chip
				size={"small"}
				clickable
				label={localizeUnsafe(status.labelKey)}
				color={status.color === "SUCCESS" ? "success" : "default"}
				onClick={(event) => {
					event.stopPropagation();
					if (status.clickable) {
						setFoundationsCompletedManuallyAndRefresh(status).catch(logError);
						return;
					}
					if (tooltipText != null) {
						setDisabledTooltipOpen(true);
						window.setTimeout(() => setDisabledTooltipOpen(false), 1800);
					}
				}}
				sx={{
					height: 20,
					backgroundColor: status.color === "SUCCESS" ? undefined : "grey.300",
					"& .MuiChip-label": {
						paddingX: 0.9,
						fontSize: "0.72rem",
						lineHeight: 1,
					},
				}}
			/>
		</Tooltip>
	);

	async function setFoundationsCompletedManuallyAndRefresh(
		currentStatus: SalvosFoundationsStatusChipProps["status"],
	) {
		const completed = currentStatus.color !== "SUCCESS";
		await SalvosCustomerOrderApi.setFoundationsCompletedManually({
			customerOrderId,
			completed,
		});
		await refreshAll();
	}
};
